/**
 * @file useFilterState.ts
 * @desc Hook for managing filter state
 * @copyright All rights reserved @ Inarix
 */
import { useState, useEffect } from "react";
import { FilterItemExtended } from "../types";
import {
  createEmptyFilterItem,
  convertIdsToNames,
  convertActionsIdsToNames,
  getColumnType,
} from "../utils/filterUtils";

interface UseFilterStateProps {
  filterModel: any;
  columns: any[];
  locations: any[];
  actions: any[];
  comesFromDashboard?: boolean;
  preselectedField?: string | null;
  setPreselectedFilterField: (field: string | null) => void;
}

export const useFilterState = ({
  filterModel,
  columns,
  locations,
  actions,
  comesFromDashboard,
  preselectedField,
  setPreselectedFilterField,
}: UseFilterStateProps) => {
  const [filterItems, setFilterItems] = useState<FilterItemExtended[]>([
    createEmptyFilterItem(),
  ]);

  // Load current filter data on initialization
  useEffect(() => {
    if (filterModel?.items?.length > 0) {
      setFilterItems(filterModel.items);
    } else {
      setFilterItems([createEmptyFilterItem()]);
    }
  }, [filterModel]);

  // Convert IDs back to names for API/LOCAL filters when locations/actions are loaded
  useEffect(() => {
    if (
      (locations && locations.length > 0 && locations[0]?.id !== undefined) ||
      (actions && actions.length > 0 && actions[0]?.id !== undefined)
    ) {
      setFilterItems((prevItems) =>
        prevItems.map((item) => {
          const column = columns.find((col) => col.field === item.field);

          // Handle API filter (locations)
          if (
            column?.filter === "API" &&
            item.operator === "$in" &&
            item.value &&
            locations &&
            locations.length > 0
          ) {
            return convertIdsToNames(item, locations, column);
          }

          // Handle API filter with queryFormat for locations dashboard
          if (
            column?.filter === "API" &&
            item.operator === "$in" &&
            item.value &&
            locations &&
            locations.length > 0 &&
            comesFromDashboard === true
          ) {
            return convertIdsToNames(item, locations, column);
          }

          // Handle LOCAL filter (actions)
          if (
            column?.filter === "LOCAL" &&
            item.operator === "$in" &&
            item.value &&
            actions &&
            actions.length > 0
          ) {
            return convertActionsIdsToNames(item, actions);
          }

          return item;
        }),
      );
    }
  }, [locations, actions, columns, comesFromDashboard]);

  // Handle preselected field
  useEffect(() => {
    if (preselectedField) {
      setFilterItems((prev) => {
        const hasExistingFilters = prev.some(
          (item) => item.field && item.field !== "",
        );

        const column = columns.find((col) => col.field === preselectedField);
        const columnType = getColumnType(columns, preselectedField);

        const newFilterItem: FilterItemExtended = {
          field: preselectedField,
          operator: "",
          value: "",
          column: "",
          realOperator: "",
          columnType: columnType,
          filter: column?.filter || "",
          service: column?.service || "",
          queryFormat: column?.queryFormat || "",
        };

        if (!hasExistingFilters && prev.length === 1) {
          return [newFilterItem];
        } else {
          return [...prev, newFilterItem];
        }
      });

      setPreselectedFilterField(null);
    }
  }, [preselectedField, setPreselectedFilterField, columns]);

  const handleFieldChange = (index: number, field: string) => {
    const newItems = [...filterItems];
    const column = columns.find((col) => col.field === field);
    newItems[index] = {
      ...newItems[index],
      field,
      operator: "",
      value: "",
      column: "",
      realOperator: "",
      columnType: getColumnType(columns, field),
      filter: column?.filter || "",
      service: column?.service || "",
      queryFormat: column?.queryFormat || "",
    };
    setFilterItems(newItems);
  };

  const handleOperatorChange = (index: number, operator: string) => {
    const newItems = [...filterItems];
    newItems[index] = {
      ...newItems[index],
      operator,
      realOperator: operator,
      column:
        columns.find((col) => col.field === newItems[index].field)
          ?.headerName || "",
      columnType: getColumnType(columns, newItems[index].field),
      filter: newItems[index].filter,
      service: newItems[index].service,
      queryFormat: newItems[index].queryFormat,
    };
    setFilterItems(newItems);
  };

  const handleValueChange = (index: number, value: string) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value };
    setFilterItems(newItems);
  };

  const handleChipValueChange = (index: number, values: string[]) => {
    const newItems = [...filterItems];
    newItems[index] = { ...newItems[index], value: values };
    setFilterItems(newItems);
  };

  const addFilter = () => {
    setFilterItems([...filterItems, createEmptyFilterItem()]);
  };

  const removeFilter = (index: number) => {
    const newItems = filterItems.filter((_, i) => i !== index);
    setFilterItems(newItems.length > 0 ? newItems : [createEmptyFilterItem()]);
  };

  const clearAllFilters = () => {
    setFilterItems([createEmptyFilterItem()]);
  };

  return {
    filterItems,
    setFilterItems,
    handleFieldChange,
    handleOperatorChange,
    handleValueChange,
    handleChipValueChange,
    addFilter,
    removeFilter,
    clearAllFilters,
  };
};
